import React, { useState } from "react";

import dayjs from "dayjs";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";

interface DynamicTableProps {
  title: string;
  report: any[];
}

const formatHeader = (key: string) =>
  key
    .replace(/_/g, " ")
    .replace(
      /\w\S*/g,
      (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    );

const isDateLike = (value: any): boolean => {
  if (typeof value !== "string") return false;

  const trimmed = value.trim();

  const isoRegex =
    /^\d{4}-(0[1-9]|1[0-2])-([0][1-9]|[12][0-9]|3[01])T([01][0-9]|2[0-3]):[0-5]\d:[0-5]\d\.\d{3}Z$/;

  if (isoRegex.test(trimmed)) {
    const parsed = Date.parse(trimmed);

    // Check if it's a valid date and not just "parsable"
    const date = new Date(trimmed);
    return !isNaN(parsed) && date.toString() !== "Invalid Date";
  }

  return false;
};

const isValidUrl = (value: any): boolean => {
  if (typeof value !== "string") return false;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const DynamicTable: React.FC<DynamicTableProps> = ({ title, report }) => {
  // All hooks must be called before any return
  const columns = Array.from(
    new Set(
      report && report.length > 0
        ? report.flatMap((row) => Object.keys(row))
        : []
    )
  );

  const dateColumns = new Set<string>();
  if (report && report.length > 0) {
    for (const col of columns) {
      for (const row of report) {
        if (isDateLike(row[col])) {
          dateColumns.add(col);
          break;
        }
      }
    }
  }

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "asc" });
  const [originalOrder] = useState(report ? report.map((_, idx) => idx) : []); // for S. No

  // Sorting logic
  const sortedReport = React.useMemo(() => {
    if (!report || report.length === 0) return [];
    if (!sortConfig.key) return report;
    if (sortConfig.key === "S.No") {
      // Reset to original order
      return originalOrder.map((i) => report[i]);
    }
    const col = sortConfig.key;
    const compare = (a: any, b: any) => {
      const valA = a[col];
      const valB = b[col];
      // Handle null/undefined
      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;
      // Date
      if (dateColumns.has(col)) {
        const dateA = new Date(valA).getTime();
        const dateB = new Date(valB).getTime();
        return dateA - dateB;
      }
      // Number
      if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
        return Number(valA) - Number(valB);
      }
      // String
      return String(valA).localeCompare(String(valB));
    };
    const sorted = [...report].sort((a, b) => {
      const res = compare(a, b);
      return sortConfig.direction === "asc" ? res : -res;
    });
    return sorted;
  }, [report, sortConfig, originalOrder, dateColumns]);

  const handleSort = (col: string | "S.No") => {
    setSortConfig((prev) => {
      if (prev.key === col) {
        // Toggle direction
        return {
          key: col,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key: col, direction: "asc" };
    });
  };

  // Now safe to conditionally render
  if (!report || report.length === 0) return <div>No data available.</div>;

  const handleDownload = () => {
    const formattedHeaders = columns.map(formatHeader);

    const rows = report.map((row) =>
      columns
        .map((col) => {
          let val = row[col];

          if (dateColumns.has(col) && val) {
            try {
              val = new Date(val).toLocaleString();
            } catch {}
          }

          // Escape CSV special characters
          const cell = String(val ?? "").replace(/"/g, '""');
          return `"${cell}"`;
        })
        .join(",")
    );

    const now = dayjs().format("YYYY-MM-DD_HH:mm:ss");

    const csvContent = [formattedHeaders.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${title + "_" + now}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <button
          onClick={handleDownload}
          style={{
            marginBottom: "4px",
            padding: "10px",
            backgroundColor: "#4A90E2",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontWeight: 600,
            fontSize: "0.7rem",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <FontAwesomeIcon icon={faDownload} fontSize="0.7rem" />
          Download CSV
        </button>
      </Box>

      <div style={{ maxHeight: window.innerHeight - 250, overflowY: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th
                style={{
                  border: "1px solid #ccc",
                  padding: "4px",
                  textAlign: "center",
                  fontSize: "0.7rem",
                  fontWeight: "normal",
                  backgroundColor: "#a1a1a1",
                  color: "#f5f5f5",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  cursor: "pointer",
                }}
                onClick={() => handleSort("S.No")}
              >
                S.No{" "}
                {sortConfig.key === "S.No"
                  ? sortConfig.direction === "asc"
                    ? "▲"
                    : "▼"
                  : ""}
              </th>
              {columns.map((col) => (
                <th
                  key={col}
                  style={{
                    border: "1px solid #ccc",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "0.7rem",
                    fontWeight: "normal",
                    backgroundColor: "#a1a1a1",
                    color: "#f5f5f5",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort(col)}
                >
                  {formatHeader(col)}{" "}
                  {sortConfig.key === col
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedReport.map((row, idx) => (
              <tr key={idx}>
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "0.7rem",
                    fontWeight: "normal",
                  }}
                >
                  {report.indexOf(row) + 1}
                </td>
                {columns.map((col) => {
                  const raw = row[col];
                  const value =
                    dateColumns.has(col) && raw
                      ? new Date(raw).toLocaleString()
                      : raw;

                  return (
                    <td
                      key={col}
                      style={{
                        border: "1px solid #ccc",
                        padding: "4px",
                        textAlign: "center",
                        fontSize: "0.7rem",
                        fontWeight: "normal",
                      }}
                    >
                      {isValidUrl(value) ? (
                        <a
                          href={value}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Link
                        </a>
                      ) : (
                        String(value ?? "")
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default DynamicTable;
